import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const CaseOffers = ({ offers, apps, id, caseRec, setOfferAcceptance, onReload }) => {
    const [editingOffer, setEditingOffer] = useState(null);
    const [viewingOffer, setViewingOffer] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const handleEdit = (offer) => {
        setEditingOffer(offer);
        setEditForm({
            offer_type: offer.offer_type || 'conditional',
            tuition_total: offer.tuition_total || '',
            issue_date: offer.issue_date || '',
            expires_at: offer.expires_at || '',
            notes: offer.metadata?.notes || ''
        });
    };

    const handleSaveEdit = async () => {
        if (!editingOffer) return;
        setSaving(true);
        try {
            await supabase.from('application_offers').update({
                offer_type: editForm.offer_type,
                tuition_total: editForm.tuition_total ? Number(editForm.tuition_total) : null,
                issue_date: editForm.issue_date || null,
                expires_at: editForm.expires_at || null,
                metadata: { ...editingOffer.metadata, notes: editForm.notes || null }
            }).eq('id', editingOffer.id);
            setEditingOffer(null);
            if (onReload) onReload();
        } catch (e) {
            console.error('Failed to update offer:', e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (offerId) => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;
        try {
            await supabase.from('application_offers').delete().eq('id', offerId);
            if (onReload) onReload();
        } catch (e) {
            console.error('Failed to delete offer:', e);
        }
    };

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex justify-between items-center">
                <h4 className="text-lg font-bold text-green-400 uppercase tracking-wider flex items-center gap-2">
                    📄 OFFERS
                </h4>
                <Link to={`/admin/offers/new?case_id=${id}${caseRec?.client_id ? `&client_id=${caseRec.client_id}` : ''}`} className="px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase tracking-wider border border-blue-500">
                    + Record Offer
                </Link>
            </div>

            {/* View Modal */}
            {viewingOffer && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingOffer(null)}>
                    <div className="bg-gray-800 border border-gray-600 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-4">Offer Details</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Type</span>
                                <span className="text-white uppercase font-bold">{viewingOffer.offer_type || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Tuition</span>
                                <span className="text-white">{viewingOffer.tuition_total || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Issue Date</span>
                                <span className="text-white">{viewingOffer.issue_date || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Expires</span>
                                <span className="text-white">{viewingOffer.expires_at || '-'}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-700 pb-2">
                                <span className="text-gray-400">Accepted</span>
                                <span className={viewingOffer.accepted ? 'text-green-400' : 'text-gray-400'}>{viewingOffer.accepted ? 'Yes' : 'No'}</span>
                            </div>
                            {viewingOffer.metadata?.scholarship_amount && (
                                <div className="flex justify-between border-b border-gray-700 pb-2">
                                    <span className="text-gray-400">Scholarship</span>
                                    <span className="text-white">{viewingOffer.metadata.scholarship_amount} {viewingOffer.metadata.currency || ''}</span>
                                </div>
                            )}
                            {viewingOffer.metadata?.notes && (
                                <div className="border-b border-gray-700 pb-2">
                                    <span className="text-gray-400 block mb-1">Notes</span>
                                    <p className="text-white text-xs">{viewingOffer.metadata.notes}</p>
                                </div>
                            )}
                        </div>
                        <button onClick={() => setViewingOffer(null)} className="mt-4 w-full px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Close</button>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingOffer && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingOffer(null)}>
                    <div className="bg-gray-800 border border-gray-600 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-white mb-4">Edit Offer</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Offer Type</label>
                                <select className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.offer_type} onChange={e => setEditForm({ ...editForm, offer_type: e.target.value })}>
                                    <option value="conditional">Conditional</option>
                                    <option value="unconditional">Unconditional</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Tuition Total</label>
                                <input type="number" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.tuition_total} onChange={e => setEditForm({ ...editForm, tuition_total: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Issue Date</label>
                                    <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.issue_date} onChange={e => setEditForm({ ...editForm, issue_date: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Expires At</label>
                                    <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.expires_at} onChange={e => setEditForm({ ...editForm, expires_at: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Notes</label>
                                <textarea rows={3} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setEditingOffer(null)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Cancel</button>
                            <button onClick={handleSaveEdit} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-900 border-b border-gray-700 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <th className="p-4">Application</th>
                            <th className="p-4">Type</th>
                            <th className="p-4">Tuition</th>
                            <th className="p-4">Expires</th>
                            <th className="p-4">Accepted</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700 font-mono text-sm">
                        {offers.map(o => (
                            <tr key={o.id} className="hover:bg-gray-700/30 transition-colors">
                                <td className="p-4 text-white font-bold">
                                    {(() => { const a = apps.find(x => x.id === o.application_id); return a?.programs?.name || a?.program || '-'; })()}
                                </td>
                                <td className="p-4 text-gray-300 uppercase">{o.offer_type || '-'}</td>
                                <td className="p-4 text-gray-300">{o.tuition_total || '-'}</td>
                                <td className="p-4 text-gray-300">
                                    {o.expires_at || '-'}
                                    {o.expires_at && (() => { const d = new Date(o.expires_at); const left = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24)); return left <= 7 ? <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-900/30 text-red-400 border border-red-800 uppercase animate-pulse">EXPIRING</span> : null; })()}
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${o.accepted ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                                        {o.accepted ? 'YES' : 'NO'}
                                    </span>
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center gap-2">
                                        <Link to={`/admin/offers/${o.id}`} className="px-2 py-1 rounded text-xs font-bold bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">View</Link>
                                        <button onClick={() => handleEdit(o)} className="px-2 py-1 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500 hover:bg-blue-500">Edit</button>
                                        {!o.accepted && (
                                            <button className="px-2 py-1 rounded text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 border border-emerald-500" onClick={() => setOfferAcceptance(o, true)}>Accept</button>
                                        )}
                                        {o.accepted && (
                                            <button className="px-2 py-1 rounded text-xs font-bold bg-gray-700 text-white border border-gray-600 hover:bg-gray-600" onClick={() => setOfferAcceptance(o, false)}>Decline</button>
                                        )}
                                        <button onClick={() => handleDelete(o.id)} className="px-2 py-1 rounded text-xs font-bold bg-red-900/50 text-red-400 border border-red-800 hover:bg-red-900">Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {offers.length === 0 && (
                            <tr>
                                <td colSpan="6" className="p-8 text-center text-gray-500 uppercase">No offers recorded</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CaseOffers;
